import { Fragment, useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '@/stores/auth'
import { X, Sparkles } from 'lucide-react'

interface KeyboardShortcutsHelpProps {
  open: boolean
  onClose: () => void
}

const shortcuts: { keys: string[]; description: string; separator?: string; adminOnly?: boolean; group?: string }[] = [
  { keys: ['Cmd', 'K'], separator: '+', description: 'Open command palette' },
  { keys: ['g', 'n'], separator: 'then', description: 'Go to notes' },
  { keys: ['g', 'p'], separator: 'then', description: 'Go to patients' },
  { keys: ['g', 'c'], separator: 'then', description: 'Go to calendar' },
  { keys: ['g', 'm'], separator: 'then', description: 'Go to messages' },
  { keys: ['g', 'i'], separator: 'then', description: 'Go to inbox' },
  { keys: ['g', 'o'], separator: 'then', description: 'Go to order' },
  { keys: ['g', 's'], separator: 'then', description: 'Go to settings' },
  { keys: ['g', 'a'], separator: 'then', description: 'Go to manage', adminOnly: true },
  { keys: ['/'], description: 'Focus search' },
  { keys: ['?'], description: 'Show keyboard shortcuts' },
  { keys: ['Esc'], description: 'Close overlay / modal' },
  { keys: ['m'], description: 'Toggle mic (during call)' },
  { keys: ['v'], description: 'Toggle camera (during call)' },
  { keys: ['Esc'], description: 'Toggle full-screen / PiP (during call)' },
  // Calendar (CLA-1298) — only while the calendar is open with nothing on top.
  { keys: ['←', '→'], separator: '/', description: 'Previous / next day, week or month', group: 'Calendar' },
  { keys: ['t'], description: 'Go to today', group: 'Calendar' },
  { keys: ['d', 'w', 'm', 'a'], separator: '/', description: 'Day / week / month / agenda view', group: 'Calendar' },
  { keys: ['n'], description: 'New appointment', group: 'Calendar' },
]

const aiQueryGroups = [
  {
    label: 'Find patients',
    examples: ['find patient Sarah', 'Sarah Chen'],
  },
  {
    label: 'Notes',
    examples: ['my notes from yesterday', 'this patient\'s last note', 'recent notes'],
  },
  {
    label: 'Schedule',
    examples: ['my schedule for tomorrow', 'who\'s next', 'appointments today'],
  },
  {
    label: 'Navigate',
    examples: ['go to settings', 'show me inventory', 'open calendar'],
  },
  {
    label: 'Orders',
    examples: ['recent orders', 'pending orders'],
  },
]

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function KeyboardShortcutsHelp({ open, onClose }: KeyboardShortcutsHelpProps) {
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const panelRef = useRef<HTMLDivElement>(null)
  const previousFocus = useRef<HTMLElement | null>(null)

  // Save and restore focus on open/close
  useEffect(() => {
    if (open) {
      previousFocus.current = document.activeElement as HTMLElement
    } else if (previousFocus.current) {
      previousFocus.current.focus()
      previousFocus.current = null
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }

    // Use capture phase so we intercept Escape before other handlers
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [open, onClose])

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return

    const panel = panelRef.current
    if (!panel) return

    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }, [])

  if (!open) return null

  const visibleShortcuts = shortcuts.filter((s) => !s.adminOnly || isAdmin)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onKeyDown={handleKeyDown}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        className="relative z-10 w-full max-w-md bg-surface border border-border-subtle rounded-xl shadow-xl p-5 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-ink">Keyboard shortcuts</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-surface-tertiary text-ink-faint transition-colors"
            aria-label="Close shortcuts"
            autoFocus
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2">
          {visibleShortcuts.map((shortcut, i) => (
            <Fragment key={shortcut.description}>
              {shortcut.group && visibleShortcuts[i - 1]?.group !== shortcut.group && (
                <div className="text-[10px] font-medium uppercase tracking-wider text-ink-faint pt-3">
                  {shortcut.group}
                </div>
              )}
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-ink-secondary">{shortcut.description}</span>
                <div className="flex items-center gap-1">
                  {shortcut.keys.map((key, i) => (
                    <span key={i}>
                      {i > 0 && shortcut.separator && (
                        <span className="text-[10px] text-ink-faint mx-0.5">{shortcut.separator}</span>
                      )}
                      <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 bg-surface-secondary border border-border-subtle rounded text-[11px] font-medium text-ink-secondary">
                        {key}
                      </kbd>
                    </span>
                  ))}
                </div>
              </div>
            </Fragment>
          ))}
        </div>

        {/* AI queries cheat sheet */}
        <div className="mt-5 pt-4 border-t border-border-subtle">
          <div className="flex items-center gap-1.5 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-ink-faint" />
            <h2 className="text-sm font-semibold text-ink">AI queries in Cmd+K</h2>
          </div>
          <p className="text-xs text-ink-faint mb-3">
            Use natural language in the command palette. AI activates automatically when your query includes words like <em>my</em>, <em>last</em>, <em>today</em>, <em>show me</em>, etc.
          </p>
          <div className="space-y-3">
            {aiQueryGroups.map((group) => (
              <div key={group.label}>
                <div className="text-[10px] font-medium uppercase tracking-wider text-ink-faint mb-1">
                  {group.label}
                </div>
                <div className="space-y-0.5">
                  {group.examples.map((example) => (
                    <div key={example} className="text-sm text-ink-secondary py-0.5">
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
